import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from 'utils/api';
import { ExtendedFilterType, ExtendedKitchenFilterType } from '../types';
import { SocketIOConnection } from 'utils/socket_client';
import { ALL_POLYGONS_ID, STORAGE_KEYS } from 'utils/constants';
import LiveTripCards from 'components/live-trip-cards';
import { PolygonsTab } from 'components/base/polygons-tab';
import { StatusTabs } from '../live-trips-header/status-tabs';
import './styles.scss';
import { OrderType } from 'models/order.model';
import { CoKitchenPolygons, CoKitchenType } from 'models/cokitchen.model';
import useRoleContext from 'contexts/role.context';
import CSTab, { CSTabListType, CSTabType } from 'components/base/cs-tab';
import { isCSRole } from 'utils/functions';
import { MailIcon, OrdersIcon, PhoneIcon, RiderAltIcon, UserIcon } from 'cokitchen-icons';
import { ENUM_ROLE } from '@cokitchen/cokitchen-auth';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ENUM_KITCHEN_STATUS } from 'utils/enum';
import NetworkIndicator from 'contexts/network.context/network-indicator';
import { getGeneralSurgeSnapshot, getPolygonSurge } from 'utils/firebase';
import SearchBar, { SearchBarType } from 'components/base/search-bar';

const SEARCH_TYPES: SearchBarType[] = [
  {
    name: 'Order code',
    value: 'order_code',
    icon: <OrdersIcon />
  },
  {
    name: 'User name',
    value: 'user_name',
    icon: <UserIcon />
  },
  {
    name: 'Rider name',
    value: 'rider_name',
    icon: <RiderAltIcon />
  },
  {
    name: 'User phone',
    value: 'user_phone',
    icon: <PhoneIcon />
  },
  {
    name: 'User email',
    value: 'user_email',
    icon: <MailIcon />
  }
];

const getLiveTrips = async (params: Record<string, unknown>, signal?: AbortSignal) => {
  const res = await apiService.get('cs/all/trips', {
    params,
    isInternal: true,
    baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY,
    signal
  });
  return isCSRole() ? res.data : res.data;
};

const POLLING_INTERVAL_MS = 50000;

const LiveTripsLiveView: FC<{ setView: () => void }> = ({ setView }) => {
  const mounted = useRef(false);
  const activeStatus = useRef('All');
  const cleanupSocket = useRef<(() => void) | null>(null);
  const latestRequestId = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const [filters, setFilters] = useState({
    status: 'All',
    search: '',
    type: SEARCH_TYPES[0].value,
    page: 1
  });
  const [searchBarValue, setSearchBarValue] = useState({ search: '', type: SEARCH_TYPES[0].value });
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderType[]>([]);
  const [activeCokitchen, setActiveCokitchen] = useState({} as CoKitchenType);
  const [activePolygon, setActivePolygon] = useState({} as CoKitchenPolygons);
  const [downloadingInitData, setDownloadingInitData] = useState(false);
  const [numberOfItems, setNumberOfItems] =
    useState<Record<ExtendedFilterType | ExtendedKitchenFilterType, number>>();
  const [activeRepTab, setActiveRepTab] = useState<CSTabType>('all');
  const [totalOrders, setTotalOrders] = useState(0);
  const [splitScreenOrders, setSplitScreenOrders] = useState<OrderType[]>([]);
  const [available, setAvailable] = useState(false);

  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN) as string;
  const parseScreenDetails = (value: string | null) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('Invalid screen details in storage', error);
      return null;
    }
  };

  const screenDetails = parseScreenDetails(localStorage.getItem(STORAGE_KEYS.SCREEN_DETAILS));

  useEffect(() => () => activeRequest.current?.abort(), []);

  const loadOrders = async (
    reload = true,
    options?: { showLoading?: boolean; reset?: boolean }
  ) => {
    const { showLoading = true, reset = reload || filters.page === 1 } = options ?? {};
    const requestId = ++latestRequestId.current;

    if (activeRequest.current) {
      activeRequest.current.abort();
    }
    const controller = new AbortController();
    activeRequest.current = controller;

    console.log('load orders', filters, reload);
    try {
      if (reset) {
        if (showLoading) setLoading(true);
        setDownloadingInitData(true);
        setSplitScreenOrders(() => []);
        setFilteredOrders(() => []);
      }
      const params = {
        cokitchen_id: cokitchenId,
        page: filters.page,
        ...(filters.status !== 'All' ? { status: filters.status } : {}),
        ...(filters.search ? { [searchBarValue.type]: filters.search } : {})
      };
      const res = await getLiveTrips(params, controller.signal);

      if (requestId === latestRequestId.current) {
        setTotalOrders(res?.total ?? 0);
        if (reload || filters.page === 1) {
          setOrders(() => {
            const orderMap = new Map();
            res.results.forEach((order: OrderType) => orderMap.set(order.id, order));
            return Array.from(orderMap.values());
          });
        } else {
          setOrders((prev) => {
            const orderMap = new Map(prev.map((o) => [o.id, o]));
            res.results.forEach((order: OrderType) => orderMap.set(order.id, order));
            return Array.from(orderMap.values());
          });
        }
      }
    } catch (error: any) {
      if (!controller.signal.aborted) throw error;
    } finally {
      if (requestId === latestRequestId.current) {
        activeRequest.current = null;
        if (reset) setDownloadingInitData(false);
        if (showLoading) setLoading(false);
      }
    }
  };

  const loadOrderStatusCounts = async () => {
    try {
      const params: { cokitchen_id: string; cokitchen_polygon_id?: string } = {
        cokitchen_id: cokitchenId
      };

      if (activePolygon.id !== ALL_POLYGONS_ID) {
        params.cokitchen_polygon_id = activePolygon.id;
      }
      const res = await apiService.get('order/status/counts', {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setNumberOfItems(res.data.data);
    } catch (e) {
      setNumberOfItems(undefined);
    }
  };

  useEffect(() => {
    if (activeRepTab === 'escalated') {
      fetchEscalatedTrips();
    } else if (activeRepTab === 'assigned') {
      fetchAssignedTrips();
    } else {
      if (downloadingInitData) return;
    }
  }, [downloadingInitData, activeRepTab]);

  const removeOrderIfExist = (order: OrderType) => {
    setOrders((prevOrders) => prevOrders.filter((item) => item.id !== order.id));
  };

  const handleUpdatedOrder = (order: { status: ExtendedKitchenFilterType; order: OrderType }) => {
    const { status, order: orderData } = order;
    const isOrderValid = status === activeStatus.current || activeStatus.current === 'All';

    if (isOrderValid) {
      setOrders((prevOrders) => {
        const hasOrder = prevOrders.some((itm) => itm.id === orderData.id);
        let updatedAllOrders = [...prevOrders];
        if (!hasOrder) {
          updatedAllOrders = [...updatedAllOrders, orderData];
        } else {
          const oldOrders = prevOrders.filter((itm) => itm.id !== orderData.id);
          updatedAllOrders = [...oldOrders, orderData];
        }
        return updatedAllOrders;
      });
    } else {
      removeOrderIfExist(orderData);
    }
  };

  const connectSocket = async () => {
    const connection = await SocketIOConnection.getConnection();
    connection.onReconnect(() => loadOrders(false));

    connection.joinRoom(`order-status-${cokitchenId}`, process.env.REACT_APP_WEBOCKETAPP_KEY);

    connection.joinRoom(`order-status-count-${cokitchenId}`, process.env.REACT_APP_WEBOCKETAPP_KEY);
    connection.addEventListener(
      `order-status-${cokitchenId}`,
      'updated',
      (order: { order: OrderType; status: ExtendedKitchenFilterType }) => {
        handleUpdatedOrder(order);
      }
    );
    connection.addEventListener(
      `order-status-count-${cokitchenId}`,
      'updated',
      (orderCount: Record<ExtendedFilterType | ExtendedKitchenFilterType, number>) => {
        setNumberOfItems(orderCount);
      }
    );

    loadOrders(true);
    cleanupSocket.current = () => {
      connection.disconnect();
    };
  };

  const fetchAssignedTrips = async () => {
    const repID = localStorage.getItem(STORAGE_KEYS.USER_ID);

    setLoading(true);
    try {
      const res = await apiService.get(`cs/assigned/all/trips/${repID}`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const assignedTrips = res.data?.data ?? [];

      setOrders(assignedTrips.map((data: Record<string, unknown>) => data.order));
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalatedTrips = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`cs/all/lead/order`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const escalatedOrders = res.data.results ?? [];
      setOrders(escalatedOrders);
    } finally {
      setLoading(false);
    }
  };

  const setAvailability = (doc: any) => {
    const { riders_available } = doc;
    setAvailable(riders_available ?? false);
  };

  useEffect(() => {
    if (activePolygon.id) {
      if (activePolygon.id === 'ALL') getGeneralSurgeSnapshot(activeCokitchen.id, setAvailability);
      else getPolygonSurge(activePolygon.id, setAvailability);
    }
  }, [activePolygon.id]);

  useEffect(() => {
    if (cokitchenId) connectSocket();
    return () => {
      if (cleanupSocket.current) cleanupSocket.current();
    };
  }, [cokitchenId]);

  useEffect(() => {
    loadOrderStatusCounts();
  }, [activePolygon]);

  const cokitchenArray: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
  );

  useEffect(() => {
    const cokitchen = cokitchenArray.find((c) => c.id === cokitchenId) ?? ({} as CoKitchenType);
    setActiveCokitchen(cokitchen);
  }, []);

  useEffect(() => {
    filterConfig();
  }, [splitScreenOrders, activePolygon?.id]);

  useEffect(() => {
    if (!screenDetails) return setSplitScreenOrders(orders);
    const filterScreenBrands = orders.filter((item) =>
      item?.calculated_order?.brand_ids?.some((itm) => screenDetails?.brand_ids?.includes(itm?.id))
    );
    setSplitScreenOrders(filterScreenBrands);
  }, [orders]);

  const filterConfig = () => {
    const polygonFilteredOrders =
      activePolygon.id === ALL_POLYGONS_ID
        ? splitScreenOrders
        : splitScreenOrders.filter(
            (order) => order.calculated_order.cokitchen_polygon_id === activePolygon.id
          );
    setFilteredOrders(polygonFilteredOrders);
  };

  const tripsCountPerPolygon = useMemo(() => {
    return splitScreenOrders?.reduce((counts, order) => {
      if (counts[order.calculated_order.cokitchen_polygon_id])
        counts[order.calculated_order.cokitchen_polygon_id] =
          counts[order.calculated_order.cokitchen_polygon_id] + 1;
      else counts[order.calculated_order.cokitchen_polygon_id] = 1;
      return counts;
    }, {} as Record<string, number>);
  }, [splitScreenOrders]);

  useEffect(() => {
    if (mounted.current || filters.page > 1) loadOrders(filters.page === 1);
    return () => {
      mounted.current = true;
    };
  }, [filters]);

  useEffect(() => {
    if (activeRepTab !== 'all') return;

    const intervalId = setInterval(() => {
      loadOrders(false, { showLoading: false, reset: false });
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [filters, activeRepTab]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((f) => ({ ...f, page: 1, ...searchBarValue }));
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [searchBarValue.search]);

  const { role } = useRoleContext();

  const isCSOutbound = () => role === ENUM_ROLE.CS_OUTBOUND;
  const isCSSuperAdmin = () => role === ENUM_ROLE.CS_SUPER_ADMIN;
  const TAB_LIST: CSTabListType[] = [
    {
      text: 'All Trips',
      value: 'all',
      className: 'rounded-l-md'
    },
    {
      text: `Escalated Trips`,
      value: 'escalated',
      className: `rounded-r-md ${isCSSuperAdmin() ? 'block' : 'hidden'}`
    },
    {
      text: `Assigned to Me`,
      value: 'assigned',
      className: `rounded-r-md ${isCSOutbound() ? 'block' : 'hidden'}`
    }
  ];

  return (
    <div key={activeCokitchen.id} className='flex flex-col gap-5 flex-wrap'>
      <div className='flex justify-between flex-col lg:flex-row gap-4'>
        <div className='flex flex-col gap-3'>
          <h1 className='font-semibold text-2xl'>
            Live on-going trips ({splitScreenOrders?.length})
          </h1>
          {(isCSSuperAdmin() || isCSOutbound()) && (
            <CSTab list={TAB_LIST} active={activeRepTab} setActive={setActiveRepTab} />
          )}
        </div>
        <div className='flex flex-col gap-4 items-end'>
          <div className='hidden md:flex items-center gap-2'>
            <a
              className='text-[#E60A2B] underline font-medium whitespace-nowrap cursor-pointer text-sm'
              onClick={setView}>
              Switch to rider view
            </a>
            <NetworkIndicator />
          </div>
          <div className='block md:hidden flex-col items-center'>
            <a
              className='text-[#E60A2B] underline font-medium whitespace-nowrap cursor-pointer text-sm'
              onClick={setView}>
              Switch to rider view
            </a>
            <div className='ml-12'>
              <NetworkIndicator />
            </div>
          </div>

          <SearchBar
            searchTypes={SEARCH_TYPES}
            value={searchBarValue}
            onChange={setSearchBarValue}
          />
        </div>
      </div>
      <div className='w-full'>
        <PolygonsTab
          className='font-medium'
          activePolygon={activePolygon}
          setActivePolygon={setActivePolygon}
          hasAll
          hideHr
          tripsCount={tripsCountPerPolygon}
        />
        <hr className='text-[#DCE2EA]' />
      </div>
      {screenDetails?.brands?.length > 0 ? (
        <div className='flex items-center'>
          <div className='  text-[16px]'>
            <span className=''>Screen {screenDetails?.name}</span>
            <span className=' text-gray-5 mr-2'> showing:</span>
          </div>

          {screenDetails?.brands?.map((item: any) => (
            <img
              alt='Item Logo'
              key={item.id}
              src={item.logo}
              className=' cursor-pointer ml-[2px] w-[30px] h-[30px] rounded-[20px]'
            />
          ))}

          <div className='inline-block relative info_dropdown'>
            <div className='border border-gray-3 rounded-full w-4 h-4 text-gray-5 flex text-xs ml-3 items-center justify-center p-1 cursor-pointer'>
              i
            </div>
            <div className='text-[#6F7376] text-sm p-2 bg-white shadow-md z-10 hidden absolute w-[250px] rounded-md mt-1 info_content '>
              Brands on the spilt screen can only be edited by the supervisor.
            </div>
          </div>
        </div>
      ) : null}

      <div className='flex flex-col lg:flex-row justify-between gap-2 lg:items-center max-w-full'>
        <StatusTabs
          filter={filters.status as ENUM_KITCHEN_STATUS}
          setActiveFilter={(status) => {
            if (loading) return;
            setFilters((f) => ({ ...f, status, page: 1 }));
            activeStatus.current = status;
          }}
          disabled={loading}
          numberOfItems={numberOfItems}
        />
      </div>
      {loading ? (
        <h2 className='font-bold text-center md:h-[200px] flex items-center mx-auto text-lg md:text-2xl'>
          Loading...
        </h2>
      ) : filteredOrders?.length === 0 ? (
        <h2 className='font-bold text-center md:h-[200px] flex items-center mx-auto text-lg md:text-2xl'>
          There are no {filters.status === 'All' ? '' : filters.status} orders to show
          {activePolygon.id !== ALL_POLYGONS_ID ? ` for ${activePolygon.name}` : ''}
        </h2>
      ) : (
        <InfiniteScroll
          hasMore={totalOrders > orders?.length}
          dataLength={orders?.length}
          loader={<p className='text-center mt-8'>Loading...</p>}
          next={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>
          <LiveTripCards orders={filteredOrders} ridersAvailable={available} />
        </InfiniteScroll>
      )}
    </div>
  );
};

export default LiveTripsLiveView;
